import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/*
 * Shared floating-card shell for booking drill-downs (Overview's "Bookinger"
 * KPI and the per-day ad rows). One popover implementation, mirroring
 * MetricInfo's pattern: portaled to <body>, fixed-positioned under the trigger,
 * 150ms open delay / 100ms close grace, Escape + outside-click close, clamped
 * to the viewport and repositioned on scroll/resize. Click toggles (touch
 * support). The card is wide — min(92vw, maxWidth) — and scrolls internally
 * (max-height 60vh) so long booking lists stay readable.
 */

const OPEN_DELAY_MS = 150;
const CLOSE_GRACE_MS = 100;
const VIEWPORT_FRACTION = 0.92;
const EDGE_PAD = 8;

export function BookingHoverCard({
  card,
  children,
  className,
  maxWidth = 600,
}: {
  /** Card body. Pass null while data is loading/errored — the trigger renders
   * but no card opens (an empty list would read as "no bookings"). */
  card: ReactNode;
  /** Trigger content. */
  children: ReactNode;
  className?: string;
  /** Card width = min(92vw, maxWidth). */
  maxWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: maxWidth,
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  // Clear any pending timers on unmount (never setState on a dead component).
  useEffect(
    () => () => {
      if (openTimer.current !== null) window.clearTimeout(openTimer.current);
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(window.innerWidth * VIEWPORT_FRACTION, maxWidth);
      const left = Math.min(
        Math.max(EDGE_PAD, r.left + r.width / 2 - width / 2),
        window.innerWidth - width - EDGE_PAD,
      );
      // Vertical flip/clamp: if the card would run off the bottom of the
      // viewport, open it above the trigger; otherwise clamp it on-screen so a
      // trigger near the bottom of a phone never opens a card off the edge.
      const cardH = cardRef.current?.offsetHeight ?? 0;
      const below = r.bottom + 6;
      const spaceBelow = window.innerHeight - below - EDGE_PAD;
      let top = below;
      if (cardH > 0 && cardH > spaceBelow && r.top - 6 > spaceBelow) {
        // More room above than below — anchor the card's bottom to the trigger top.
        top = Math.max(EDGE_PAD, r.top - 6 - cardH);
      } else {
        top = Math.min(below, Math.max(EDGE_PAD, window.innerHeight - cardH - EDGE_PAD));
      }
      setPos({ top, left, width });
    };
    place();
    // Re-place once the card has mounted/measured (height is unknown on the
    // first synchronous pass) so the vertical flip/clamp uses the real height.
    const raf = window.requestAnimationFrame(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || cardRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, maxWidth]);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleOpen = () => {
    cancelClose();
    if (openTimer.current !== null) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_GRACE_MS);
  };
  const toggle = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    cancelClose();
    setOpen((o) => !o);
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: a real <button> can't wrap arbitrary trigger content (block-level divs like StatCard violate button's phrasing-content model); Enter/Space toggle + Escape close are wired to match.
    <div
      ref={triggerRef}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn('cursor-help', className)}
    >
      {children}
      {open && card !== null && card !== undefined
        ? createPortal(
            <div
              ref={cardRef}
              role="tooltip"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: pos.width,
                maxHeight: '60vh',
              }}
              className="z-50 overflow-y-auto rounded-md border border-border bg-surface p-3 text-left shadow-pop"
            >
              {card}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
